/**
 * "Suggest texts" bij het berichtveld van een Mass Message (07-10-2026, Jordy).
 *
 * Besluit Jordy en Zilvester: AI mag teksten VOORSTELLEN. Een mens leest elke
 * tekst na, past hem aan en verstuurt hem handmatig. De knop "Use in message"
 * zet een voorstel alleen in het berichtveld van dit slot; versturen blijft
 * de bestaande handmatige knop (met SEND). Hier wordt nooit iets verstuurd of
 * ingepland.
 *
 * Model en platform komen uit het slot. F2F-voorstellen zijn Nederlands
 * (de automatische vertaling gaat dan uit), FanCentro-voorstellen Engels.
 * De knop staat er alleen als de server MM_AI_SUGGESTIES én de schakelaar van
 * dat platform (MM_AI_FC / MM_AI_F2F) aan heeft; "Explicit" alleen met
 * MM_AI_EXPLICIET (`GET /api/admin/mass-ai/status`).
 */
import { useState } from 'react'
import { Modal } from './Modal'
import { Seg } from './ui'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'

interface Stand {
  enabled: boolean
  explicit: boolean
  levels: string[]
  platforms: string[]
  provider: string | null
}

interface Uitkomst {
  suggestions: string[]
  message: string | null
}

export interface AiModel {
  slug: string
  platform: 'fancentro' | 'f2f'
  label: string
}

const NIVEAU_NAAM: Record<string, string> = { teasing: 'Teasing', flirty: 'Flirty', explicit: 'Explicit' }
export const GEEN_VOORSTELLEN = 'No suggestions available, write manually'
export const NALEZEN = 'Review and rewrite before sending. Never send unedited.'

/** De knop naast het berichtveld; niets als de schakelaars uit staan. */
export function MmAiVeldKnop({ model, onGebruik }: {
  model: AiModel | null | undefined
  onGebruik: (tekst: string) => void
}) {
  const stand = useApi<Stand>('/api/admin/mass-ai/status')
  const [open, setOpen] = useState(false)
  const s = stand.data
  if (!s?.enabled || !(s.platforms ?? []).length) return null
  const platformAan = !model || s.platforms.includes(model.platform)
  if (!platformAan) return null
  return (
    <>
      <button type="button" className="btn ghost sm" data-testid="mm-ai-knop" disabled={!model}
              title={model ? 'AI suggestions for this message' : 'Choose a model first'}
              onClick={() => setOpen(true)}>
        Suggest texts
      </button>
      {open && model ? (
        <MmAiDialoog model={model} levels={s.levels}
                     onGebruik={(t) => { onGebruik(t); setOpen(false) }}
                     onClose={() => setOpen(false)} />
      ) : null}
    </>
  )
}

export function MmAiDialoog({ model, levels, onGebruik, onClose }: {
  model: AiModel
  levels: string[]
  onGebruik: (tekst: string) => void
  onClose: () => void
}) {
  const [omschrijving, setOmschrijving] = useState('')
  const [niveau, setNiveau] = useState(levels[0] ?? 'teasing')
  const [bezig, setBezig] = useState(false)
  const [voorstellen, setVoorstellen] = useState<string[] | null>(null)
  const [melding, setMelding] = useState<string | null>(null)

  const vraag = async () => {
    setBezig(true)
    setMelding(null)
    setVoorstellen(null)
    try {
      const r = await api.post<Uitkomst>('/api/admin/mass-ai/suggestions', {
        model_slug: model.slug, platform: model.platform, description: omschrijving, level: niveau,
      })
      setVoorstellen(r.suggestions ?? [])
      setMelding(r.suggestions?.length ? null : (r.message || GEEN_VOORSTELLEN))
    } catch {
      setVoorstellen([])
      setMelding(GEEN_VOORSTELLEN)
    } finally {
      setBezig(false)
    }
  }

  const kolom = { display: 'flex', flexDirection: 'column' as const }
  return (
    <Modal
      title="Suggest texts"
      width={620}
      onClose={onClose}
      desc={<span>{model.label} · {model.platform === 'f2f' ? 'F2F (Dutch)' : 'FanCentro (English)'}.
        A suggestion only goes into the message field; you send it yourself. Add prices yourself.</span>}
      footer={(
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Close</button>
          <button type="button" className="btn primary" data-testid="mm-ai-vraag" disabled={bezig} onClick={vraag}>
            {bezig ? 'Asking…' : 'Suggest'}
          </button>
        </>
      )}
    >
      <div style={{ ...kolom, gap: 10 }}>
        <div className="openbar u-warn" role="note" data-testid="mm-ai-nalezen"><b>{NALEZEN}</b></div>
        <label style={{ ...kolom, gap: 4 }}>
          <span className="mut2">What is in the content?</span>
          <textarea className="inp" rows={3} maxLength={600} value={omschrijving} data-testid="mm-ai-omschrijving"
                    placeholder="e.g. 5 photos in red lingerie on the bed"
                    onChange={(e) => setOmschrijving(e.target.value)} />
        </label>
        <div style={{ ...kolom, gap: 4 }}>
          <span className="mut2">Level</span>
          <Seg options={levels.map((l) => ({ value: l, label: NIVEAU_NAAM[l] ?? l }))}
               value={niveau} onChange={setNiveau} />
        </div>
        {melding ? <div className="openbar u-warn" data-testid="mm-ai-melding">{melding}</div> : null}
        {voorstellen && voorstellen.length ? (
          <div style={{ ...kolom, gap: 8 }} data-testid="mm-ai-voorstellen">
            {voorstellen.map((t, i) => (
              <div key={i} style={{ ...kolom, gap: 4 }}>
                <textarea className="inp" rows={3} value={t} data-testid={`mm-ai-voorstel-${i}`}
                          onChange={(e) => setVoorstellen((v) => (v ?? []).map((x, j) => (j === i ? e.target.value : x)))} />
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button type="button" className="btn sm" data-testid={`mm-ai-gebruik-${i}`}
                          disabled={!t.trim()} onClick={() => onGebruik(t.trim())}>
                    Use in message
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
